import _ from "lodash";
import ACLStore from "lib/aclStore";

import { ORG_ADD_ONS_MAPPING_V2 } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";

import {
  ACADEMIC_YEAR_TRANSITION,
  ACTIVITY_TRACKER,
  ADD_ADMIN_TO_ALL_CLASSES,
  AI_GRADING,
  AI_TUTOR,
  IMPORT_UNIT_FROM_OTHER_CURRICULUM,
  BEHAVIOUR_MANAGEMENT,
  CROSS_CURRICULUM_STUDENTS,
  CURRICULUM_PROGRAM,
  DP_1_AND_DP_2_STUDENTS,
  IBIS_REGISTRATION_REVAMP,
  IN_APP_TRANSLATION,
  ORGANIZATION,
  STUDENT_PRONOUNS,
  TODDLE_LIBRARY,
  WORKRAMP_ACADEMY,
  WORKSHEET,
  WORKSHEET_AI,
  LIBRARY,
  ADD_ON_TYPES,
  ENABLE,
  DISABLE,
  WORKSHEET_BASIC,
  AUTH_AND_EVAL,
  TODDLE_ANALYTICS,
  TODDLE_AI,
  MESSAGING,
  BLUEPRINT_SYNC,
} from "./AddOnManagerConstants";
import {
  TODDLE_1p0,
  TODDLE_2p0,
} from "InternalTools/modules/InternalToolConstants";
import { CURRICULUM_PLAN_ID_MAP } from "Constants";
import { SOURCE } from "../components/AddOnsDatagrid/modules/AddonsDatagridUtils";

export const getPageInfo = ({ isToddle20Flow, t }) => {
  const entity = isToddle20Flow
    ? t("common:account_title")
    : t("common:organization");

  const subEntity = isToddle20Flow
    ? t("common:school")
    : t("common:curriculum");

  return {
    tableOneHeading: t("onBoarding:entity_level_addons", { entity }),
    tableOneInfo: t("onBoarding:entity_level_addons_info", {
      entity: entity,
    }),
    tableTwoHeading: t("onBoarding:entity_level_addons", { entity: subEntity }),
    tableTwoInfo: t("onBoarding:entity_level_addons_info", {
      entity: subEntity,
    }),
  };
};

export const getAddOnOptions = () => {
  const showAllAddons = ACLStore.can("FeatureFlag:AddOnAllPerms");

  const [organizationAddOns, curriculumAddOns] = _.partition(
    ORG_ADD_ONS_MAPPING_V2,
    ({ addOnType }) => addOnType === ADD_ON_TYPES.ORGANIZATION_LEVEL
  );

  const filterByPermission = list =>
    showAllAddons
      ? list
      : _.filter(list, ({ perm }) => (perm ? ACLStore.can(perm) : true));

  const filteredOrganizationAddOns = filterByPermission(organizationAddOns);
  const filteredCurriculumAddOns = filterByPermission(curriculumAddOns);

  return {
    organizationAddOns: filteredOrganizationAddOns,
    curriculumAddOns: filteredCurriculumAddOns,
    allAddOns: _.concat(filteredOrganizationAddOns, filteredCurriculumAddOns),
  };
};

export const getCurriculumPrograms = ({ organizationDetails }) => {
  const curriculumOptions = _.get(organizationDetails, "curriculumOptions", []);

  return _.filter(
    curriculumOptions,
    ({ programType }) =>
      _.isEqual(programType, "SCHOOL") || _.isNil(programType)
  );
};

export const getPreEnabledEntities = ({
  addOn,
  addOnStatus = {},
  isCreateFlow,
  orgPlan,
  curriculumPrograms = [],
  orgId,
  isToddle2Org,
}) => {
  const {
    addOnType,
    enabledWithPlans = [],
    preEnableForOrgVersions = [TODDLE_1p0, TODDLE_2p0],
  } = addOn;

  const orgVersion = isToddle2Org ? TODDLE_2p0 : TODDLE_1p0;
  const isOrgVersionEligible = _.includes(preEnableForOrgVersions, orgVersion);

  const isPlanChange = addOnStatus?.source === SOURCE.PLAN_CHANGE;

  const addedEntities = [];
  const removedEntities = [];

  // CREATE FLOW PRE-ENABLE LOGIC

  if (isCreateFlow && isOrgVersionEligible) {
    if (addOnType === ADD_ON_TYPES.CURRICULUM_LEVEL) {
      const curriculumStatus = _.get(addOnStatus, "curriculumStatus", []);
      const curriculumStatusMap = _.keyBy(curriculumStatus, "id");
      const eligibleCurriculums = curriculumPrograms
        .filter(program => {
          const planId =
            _.get(program, "planId") || _.get(program, "subscriptionPlan.id");
          return (
            enabledWithPlans.includes(planId) &&
            !_.get(curriculumStatusMap, [program.id, "checked"], false) // we will add only curriculums that are not already checked in backend
          );
        })
        .map(program => program.id);

      addedEntities.push(...eligibleCurriculums);
    } else {
      if (enabledWithPlans.includes(orgPlan)) {
        addedEntities.push(orgId);
      }
    }
  }

  // PLAN CHANGE — CURRICULUM LEVEL

  if (!isCreateFlow && addOnType === ADD_ON_TYPES.CURRICULUM_LEVEL) {
    const curriculumStatus = addOnStatus?.curriculumStatus ?? [];
    const curriculumStatusMap = _.keyBy(curriculumStatus, "id");

    curriculumStatus.forEach(status => {
      if (status.source !== SOURCE.PLAN_CHANGE) return;

      if (status.isPromoted) addedEntities.push(status.id);
      if (status.isDemoted) removedEntities.push(status.id);
      if (
        status.isNewSchool &&
        !_.get(curriculumStatusMap, [status.id, "checked"], false) // we will add only curriculums that are not already checked in backend
      )
        addedEntities.push(status.id);
    });
  }

  // PLAN CHANGE — ORG LEVEL

  if (!isCreateFlow && addOnType !== ADD_ON_TYPES.CURRICULUM_LEVEL) {
    if (isPlanChange && addOnStatus?.isPromoted) {
      addedEntities.push(orgId);
    }

    if (isPlanChange && addOnStatus?.isDemoted) {
      removedEntities.push(orgId);
    }
  }

  return {
    addedEntities,
    removedEntities,
  };
};

const {
  ULTIMATE_PILOT,
  ULTIMATE,
  ESSENTIALS,
  ESSENTIALS_PILOT,
  PRO,
  PRO_PILOT,
  ADVANCE,
  ADVANCE_PILOT,
} = CURRICULUM_PLAN_ID_MAP;

export const getOrgPlan = ({ curriculumPrograms }) => {
  // Org plan is determined by the highest plan among its curriculum programs
  const planPriority = [
    ULTIMATE_PILOT,
    ULTIMATE,
    ADVANCE_PILOT,
    ADVANCE,
    ESSENTIALS_PILOT,
    ESSENTIALS,
    PRO_PILOT,
    PRO,
  ];

  for (const plan of planPriority) {
    const found = _.some(curriculumPrograms, p => {
      const planId = _.get(p, "planId") || _.get(p, "subscriptionPlan.id");
      return planId === plan;
    });

    if (found) return plan;
  }

  return null;
};

/**
 * Calculate the changes (addedEntities and removedEntities) for each add-on
 * based on the current configuration state and the initial add-ons status.
 * This util checks actual changes by comparing the current state with the initial
 * (backend) checked status of each add-on.
 */
const _calculateChanges = (configurationState, addOnsStatus) => {
  const changes = {};

  _.forEach(configurationState, (config, addOnId) => {
    const addOn = _.get(addOnsStatus, addOnId, {});
    const addOnType = _.get(addOn, "addOnType", null);
    const added = _.get(config, "addedEntities", []);
    const removed = _.get(config, "removedEntities", []);

    if (_.isEqual(addOnType, ADD_ON_TYPES.ORGANIZATION_LEVEL)) {
      const wasEnabled = _.get(addOn, "checked", false);
      const isOrgAddonDemoted = _.get(addOn, "isDemoted", false);
      const isOrgAddonPromoted = _.get(addOn, "isPromoted", false);

      const enabledEntities = wasEnabled && !isOrgAddonPromoted ? [] : added;
      const disabledEntities = wasEnabled || isOrgAddonDemoted ? removed : [];

      if (!_.isEmpty(enabledEntities) || !_.isEmpty(disabledEntities)) {
        changes[addOnId] = { enabledEntities, disabledEntities };
      }
    } else {
      const curriculumStatus = _.get(addOn, "curriculumStatus", []);

      const programsToEnable = [];
      const programsToDisable = [];

      _.forEach(curriculumStatus, program => {
        if (
          _.includes(added, program.id) &&
          (!program.checked || program?.isPromoted || program?.isNewSchool)
        ) {
          programsToEnable.push(program.id);
        } else if (
          _.includes(removed, program.id) &&
          (program.checked || program?.isDemoted)
        ) {
          programsToDisable.push(program.id);
        }
      });

      if (!_.isEmpty(programsToEnable) || !_.isEmpty(programsToDisable)) {
        changes[addOnId] = {
          enabledEntities: programsToEnable,
          disabledEntities: programsToDisable,
        };
      }
    }
  });

  return changes;
};

const _getParamsForOrgAddOns = ({
  addOnId,
  configurationState,
  orgId,
  orgRegion,
  enabledModules,
}) => {
  const addOnConfig = _.get(configurationState, addOnId, {});
  const addOnParms = _.get(addOnConfig, "addOnsParameters", {});
  const isChecked = _.includes(_.get(addOnConfig, "addedEntities", []), orgId);

  switch (addOnId) {
    case BEHAVIOUR_MANAGEMENT:
    case STUDENT_PRONOUNS:
    case DP_1_AND_DP_2_STUDENTS:
    case IBIS_REGISTRATION_REVAMP:
    case ADD_ADMIN_TO_ALL_CLASSES:
    case CROSS_CURRICULUM_STUDENTS:
    case BLUEPRINT_SYNC:
    case ACADEMIC_YEAR_TRANSITION: {
      return {
        orgId,
        addOnType: addOnId,
        updateOn: ORGANIZATION,
        newStatus: isChecked,
      };
    }
    case IN_APP_TRANSLATION: {
      return {
        orgId,
        addOnType: IN_APP_TRANSLATION,
        moduleId: IN_APP_TRANSLATION,
        updateOn: ORGANIZATION,
        newStatus: isChecked,
      };
    }
    case WORKRAMP_ACADEMY: {
      return {
        orgId,
        orgRegion,
        addOnsParameters: addOnParms,
        addOnId,
        value: isChecked,
      };
    }
    case MESSAGING: {
      const input = buildAddonStateChangePayload({
        addonScope: "GLOBAL",
        enabledModules,
      });
      return {
        input,
        addOnType: MESSAGING,
      };
    }

    default:
      return {};
  }
};

const _getParamsForCurriculumAddOns = ({
  addOnId,
  orgId,
  curriculumPrograms,
}) => {
  const getEnabledAndDisabledPrograms = () => {
    const enabledPrograms = [];
    const disabledPrograms = [];

    _.forEach(curriculumPrograms, program => {
      if (program.checked) {
        enabledPrograms.push(program.id);
      } else {
        disabledPrograms.push(program.id);
      }
    });

    return { enabledPrograms, disabledPrograms };
  };

  const commonParams = {
    orgId,
    addOnType: addOnId,
    moduleId: addOnId,
    updateOn: CURRICULUM_PROGRAM,
  };

  switch (addOnId) {
    case TODDLE_LIBRARY: {
      const permissions = _.map(curriculumPrograms, program => {
        return {
          curriculumProgramId: program.id,
          curriculumProgramUid: program.uid,
          value: program.checked,
        };
      });

      return {
        addOnType: LIBRARY,
        orgId,
        permissions,
        additionalInput: { addedGrades: [], removedGrades: [] },
      };
    }
    // Mutation support either ENABLE or DISABLE at once for multiple curriculum programs
    case WORKSHEET: {
      const {
        enabledPrograms,
        disabledPrograms,
      } = getEnabledAndDisabledPrograms();
      const payload = [];
      if (!_.isEmpty(enabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: ENABLE,
          curriculumId: enabledPrograms,
          moduleId: WORKSHEET_BASIC,
        });
      }

      if (!_.isEmpty(disabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: DISABLE,
          curriculumId: disabledPrograms,
          moduleId: WORKSHEET_BASIC,
        });
      }

      return payload;
    }
    case WORKSHEET_AI: {
      const {
        enabledPrograms,
        disabledPrograms,
      } = getEnabledAndDisabledPrograms();
      const payload = [];
      if (!_.isEmpty(enabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: ENABLE,
          curriculumId: enabledPrograms,
          moduleId: WORKSHEET_AI,
        });
      }

      if (!_.isEmpty(disabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: DISABLE,
          curriculumId: disabledPrograms,
          moduleId: WORKSHEET_AI,
        });
      }

      return payload;
    }
    case AI_TUTOR:
    case AI_GRADING:
    case IMPORT_UNIT_FROM_OTHER_CURRICULUM:
    case ACTIVITY_TRACKER: {
      const {
        enabledPrograms,
        disabledPrograms,
      } = getEnabledAndDisabledPrograms();

      const payload = [];

      if (!_.isEmpty(enabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: ENABLE,
          curriculumId: enabledPrograms,
        });
      }

      if (!_.isEmpty(disabledPrograms)) {
        payload.push({
          ...commonParams,
          newStatus: DISABLE,
          curriculumId: disabledPrograms,
        });
      }

      return payload;
    }
    case AUTH_AND_EVAL: {
      const entityGroups = _.map(curriculumPrograms, curriculumProgram => [
        {
          entityId: curriculumProgram.id,
          entityType: "CURRICULUM_PROGRAM",
        },
      ]);

      return [
        {
          addOnType: AUTH_AND_EVAL,
          permissions: [
            {
              entityGroups,
              value: false,
              valueType: "BOOLEAN",
            },
          ],
        },
      ];
    }

    case TODDLE_ANALYTICS: {
      const permissions = _.map(curriculumPrograms, curriculumProgram => ({
        curriculumProgramId: curriculumProgram?.id,
        disabledModules: curriculumProgram?.enabledModules,
      }));
      return {
        addOnType: TODDLE_ANALYTICS,
        permissions,
      };
    }

    case TODDLE_AI: {
      const input = buildAddonStateChangePayload({
        curriculumOptions: curriculumPrograms,
        addonScope: "CURRICULUM_PROGRAM",
      });
      return {
        addOnType: TODDLE_AI,
        input,
      };
    }
    default:
      return {};
  }
};

export const prepareInputFromConfigurationState = (
  configurationState,
  addOnsStatus,
  orgId,
  orgRegion
) => {
  const changes = _calculateChanges(configurationState, addOnsStatus);
  const payloadForAddOns = {};

  // Iterate over each add-on with changes to prepare mutation input
  _.forEach(changes, (entry, addOnId) => {
    const {
      enabledEntities: addedEntities,
      disabledEntities: removedEntities,
    } = entry;

    const addOn = _.get(addOnsStatus, addOnId, {});
    const addOnType = _.get(addOn, "addOnType", null);
    const enabledModules = _.get(addOn, "enabledModules", []);
    const curriculumStatus = _.get(addOn, "curriculumStatus", []);

    let mutationInput = {};

    if (_.isEqual(addOnType, ADD_ON_TYPES.ORGANIZATION_LEVEL)) {
      mutationInput = _getParamsForOrgAddOns({
        addOnId,
        configurationState,
        orgId,
        orgRegion,
        enabledModules,
      });
    } else {
      const updatedCurriculums = _.chain(curriculumStatus)
        .filter(
          program =>
            _.includes(addedEntities, program.id) ||
            _.includes(removedEntities, program.id)
        )
        .map(program => ({
          ...program,
          checked: _.includes(addedEntities, program.id),
        }))
        .value();

      mutationInput = _getParamsForCurriculumAddOns({
        addOnId,
        curriculumPrograms: updatedCurriculums,
        orgId,
      });
    }

    if (!_.isEmpty(mutationInput)) payloadForAddOns[addOnId] = mutationInput;
  });
  return payloadForAddOns;
};

/**  This function takes the current add-on statuses and the modified school plan details to determinehow the add-on statuses
 * should be updated after a plan change. It checks if each add-on is allowed in the new plan and updates its status accordingly,
 * marking it as promoted or demoted if there is a change.
 */
export const getCurriculumStatusesAfterPlanChange = ({
  entityStatuses = [],
  modifiedSchoolPlanDetails = [],
  addOn,
  isConfigurable,
  addOnType,
  needsConfiguration,
}) => {
  if (!_.isArray(entityStatuses) || !_.isArray(modifiedSchoolPlanDetails)) {
    return entityStatuses;
  }

  const { enabledWithPlans = [] } = addOn;

  return _.map(entityStatuses, status => {
    const isChecked = _.get(status, "checked", false);

    const schoolDetails = _.find(
      modifiedSchoolPlanDetails,
      change => change.id === status.id
    );

    const isNewSchool = _.get(schoolDetails, "isNewSchool", false);

    if (!schoolDetails && !isNewSchool) return status;

    const isAllowedInNewPlan = _.includes(
      enabledWithPlans,
      schoolDetails.currentPlanId
    );

    if (isNewSchool && isAllowedInNewPlan) {
      return {
        ...status,
        isConfigurable,
        needsConfiguration,
        addOnType,
        isNewSchool: true,
        source: SOURCE.PLAN_CHANGE,
      };
    }

    if (!isAllowedInNewPlan && isChecked && !isNewSchool) {
      return {
        ...status,
        isDemoted: true,
        isConfigurable,
        needsConfiguration,
        addOnType,
        source: SOURCE.PLAN_CHANGE,
      };
    }

    if (isAllowedInNewPlan && !isChecked && !isNewSchool) {
      return {
        ...status,
        isPromoted: true,
        needsConfiguration,
        isConfigurable,
        addOnType,
        source: SOURCE.PLAN_CHANGE,
      };
    }

    return status;
  });
};

/** Similar to getCurriculumStatusesAfterPlanChange but for organization-level add-ons. It checks if the add-on is allowed
 * in the new plan and updates its status accordingly, marking it as promoted or demoted if there is a change.
 */
export const getOrgAddonStatusAfterPlanChange = ({
  orgPlan,
  addOn,
  isChecked,
  enabledModules,
  modifiedSchoolPlanDetails,
}) => {
  const {
    id: addOnId,
    isConfigurable = true,
    addOnType,
    enabledWithPlans = [],
  } = addOn;

  const isAllowedInNewPlan = _.includes(enabledWithPlans, orgPlan);
  const hasSchoolPlanChanges = !_.isEmpty(modifiedSchoolPlanDetails);

  const defaultStatus = {
    addOnId,
    checked: isChecked,
    enabledModules,
    isConfigurable,
    addOnType,
  };

  // If there are no plan changes, return the default status without any promotion or demotion
  if (!hasSchoolPlanChanges) return defaultStatus;

  // If the addon is allowed in the new plan but was not checked before, it means it's promoted and we should pre-enable it
  if (isAllowedInNewPlan && !isChecked) {
    return {
      ...defaultStatus,
      isPromoted: true,
      source: SOURCE.PLAN_CHANGE,
    };
  }

  // If the addon is not allowed in the new plan but was checked before, it means it's demoted and we should disable it
  if (!isAllowedInNewPlan && isChecked) {
    return {
      ...defaultStatus,
      isDemoted: true,
      source: SOURCE.PLAN_CHANGE,
    };
  }

  return defaultStatus;
};

const buildAddonStateChangePayload = ({
  state = "DISABLE",
  addonScope,
  curriculumOptions = [],
  enabledModules = [],
}) => {
  // Curriculum Level
  if (!_.isEmpty(curriculumOptions)) {
    return _.flatMap(curriculumOptions, curriculum => {
      const curriculumId = _.get(curriculum, "id");
      const modules = _.get(curriculum, "enabledModules", []);

      return _.map(modules, moduleId => ({
        state, // "ENABLE" or "DISABLE"
        curriculumProgramIds: [curriculumId],
        moduleId,
        type: addonScope,
      }));
    });
  }

  // Organization Level
  return _.map(enabledModules, moduleId => ({
    state,
    moduleId,
    type: addonScope,
  }));
};

/** Groups add-ons by their associated curriculum. toggleCurriculumAddons is an array of toggled curriculum add-ons.
 *  toggleCurriculumAddons = [{
 *    curriculumId: string,
 *    addOnId: string,
 *    status: "enabled" | "disabled"
 *  }]
 */
export const groupAddonsByCurriculum = ({
  toggledCurriculumAddons = [],
  curriculumList = [],
}) => {
  const curriculumMap = _.keyBy(curriculumList, "id");
  const grouped = {};

  const ensureCurriculum = curriculumId => {
    if (!grouped[curriculumId]) {
      grouped[curriculumId] = {
        curriculumId,
        label: curriculumMap[curriculumId]?.label,
        addons: [],
      };
    }
  };

  toggledCurriculumAddons.forEach(({ curriculumId, addOnId, status }) => {
    if (!curriculumId || !addOnId) return;

    ensureCurriculum(curriculumId);

    grouped[curriculumId].addons.push({
      addOnId,
      status,
    });
  });

  return Object.values(grouped);
};

/** This function generates a summary of toggled add-ons for confirmation modal based on the current configuration state and the
 * initial add-on statuses. It identifies which organization-level and curriculum-level add-ons have been enabled or disabled,
 * and returns a structured summary of these changes.
 */
export const getAddonsToggleSummary = ({
  configurationState = {},
  addOnsStatus = {},
}) => {
  return _.reduce(
    configurationState,
    (res, addon, key) => {
      const { addOnType, addedEntities = [], removedEntities = [] } =
        addon || {};

      const addOnStatus = addOnsStatus?.[key];
      if (!addOnStatus) return res;

      // ======================
      // ORGANIZATION LEVEL
      // ======================
      if (addOnType === ADD_ON_TYPES.ORGANIZATION_LEVEL) {
        const { checked = false, source } = addOnStatus;

        const isEnabled =
          addedEntities.length || (source === SOURCE.QUERY && checked);

        const isDisabled =
          removedEntities.length || (source === SOURCE.QUERY && !checked);

        if (isEnabled || isDisabled) {
          res.toggledOrgAddons.push({
            addOnId: key,
            status: isEnabled ? "enabled" : "disabled",
          });
        }
      }

      // ======================
      // CURRICULUM LEVEL
      // ======================
      if (addOnType === ADD_ON_TYPES.CURRICULUM_LEVEL) {
        const { curriculumStatus = [] } = addOnStatus;

        curriculumStatus.forEach(
          ({
            id: curriculumId,
            checked: curriculumChecked = false,
            source,
          }) => {
            const isAdded = addedEntities.includes(curriculumId);
            const isRemoved = removedEntities.includes(curriculumId);

            const isEnabled =
              (isAdded && !curriculumChecked) ||
              (source === SOURCE.QUERY && curriculumChecked);

            const isDisabled =
              (isRemoved && curriculumChecked) ||
              (source === SOURCE.QUERY && !curriculumChecked);

            if (isEnabled || isDisabled) {
              res.toggledCurriculumAddons.push({
                addOnId: key,
                curriculumId,
                status: isEnabled ? "enabled" : "disabled",
              });
            }
          }
        );
      }

      return res;
    },
    {
      toggledOrgAddons: [],
      toggledCurriculumAddons: [],
    }
  );
};
